import { useMutation } from '@tanstack/react-query';
import { Minus, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from 'shared/ui/button';
import { Input } from 'shared/ui/input';
import { TooltipWrapper } from 'shared/ui/tooltips';
import type { MutationOptionsProps } from 'features/start-stop-project/ui/start-stop-project';
import type { Project } from '@/enteties/project/modal';
import type { StatusResponse } from 'shared/api/services/types';
import { useProjectQuerySlice } from 'features/start-stop-project/hooks/useProjectQuerySlice';
import * as m from '@paraglide/messages';
interface InstanceControllerProps<TData extends StatusResponse> extends MutationOptionsProps<TData> {
  withLabel?: boolean;
  slug: string;
  project: Project;
  isTimedOut: boolean;
}
export const InstanceController = <TData extends StatusResponse,>({
  mutationOptions,
  withLabel = true,
  project,
  slug,
  isTimedOut
}: InstanceControllerProps<TData>) => {
  const {
    setProjectProperty,
    setProjectArrayProperty
  } = useProjectQuerySlice(slug);
  const {
    mutate: changeInstances
  } = useMutation({
    ...mutationOptions,
    onSuccess: (data, instance) => {
      if (data.status === 'OK') {
        setProjectProperty({
          requiredInstances: instance
        });
        setProjectArrayProperty(proj => proj.slug === project.slug, {
          requiredInstances: instance
        });
      }
    }
  });
  const [defaultCount, setDefaultCount] = useState<number | ''>(1);
  const [count, setCount] = useState<number | ''>(1);
  const [showConfirm, setShowConfirm] = useState(false);
  useEffect(() => {
    setCount(project.requiredInstances);
    setDefaultCount(project.requiredInstances);
  }, [project?.requiredInstances]);
  useEffect(() => {
    setShowConfirm(count !== defaultCount && count !== '');
  }, [count, defaultCount]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue === '') {
      setCount(0);
    } else {
      const parsedValue = parseInt(newValue, 10);
      if (!Number.isNaN(parsedValue) && parsedValue >= 0) {
        setCount(parsedValue);
      }
    }
  };
  const increment = () => setCount(prev => prev === '' ? 1 : prev + 1);
  const decrement = () => setCount(prev => prev === '' ? 0 : Math.max(0, prev - 1));
  const confirm = () => {
    setShowConfirm(false);
    setDefaultCount(count);
    changeInstances(count as number);
  };
  const isDisabled = useMemo(() => isTimedOut, [isTimedOut]);
  return <div data-sentry-component="InstanceController" data-sentry-source-file="instance-controller.tsx">
      {withLabel && <p className="text-center text-caption font-medium">
          {m.project_ctrl_btns_replicas()}
        </p>}
      <div className="relative flex flex-col items-center space-y-2">
        <div className="flex items-center gap-1 rounded-lg">
          <Button variant="ghost" size="icon-highlight" onClick={decrement} disabled={isDisabled} data-sentry-element="Button" data-sentry-source-file="instance-controller.tsx">
            <Minus className="size-4" data-sentry-element="Minus" data-sentry-source-file="instance-controller.tsx" />
          </Button>
          <div className="flex flex-row items-end gap-1">
            <TooltipWrapper content={m.project_ctrl_btns_replicas_tooltip_cur()} data-sentry-element="TooltipWrapper" data-sentry-source-file="instance-controller.tsx">
              <p className="mb-0.5 w-5 pl-1 pr-2 text-center text-body2">
                {project?.instances ?? 0}
              </p>
            </TooltipWrapper>
            <p>/</p>
            <div className="w-5">
              <TooltipWrapper content={m.project_ctrl_btns_replicas_tooltip_req()} data-sentry-element="TooltipWrapper" data-sentry-source-file="instance-controller.tsx">
                <Input maxLength={2} size={2} variant="ghost" inputSize="sm" type="text" inputMode="numeric" pattern="[0-9]*" value={count} onChange={handleChange} className="size-5 px-0.5 py-0 text-center text-body2 bg-inherit" disabled={isDisabled} data-sentry-element="Input" data-sentry-source-file="instance-controller.tsx" />
              </TooltipWrapper>
            </div>
          </div>

          <Button variant="ghost" size="icon-highlight" onClick={increment} disabled={isDisabled} data-sentry-element="Button" data-sentry-source-file="instance-controller.tsx">
            <Plus className="size-4" data-sentry-element="Plus" data-sentry-source-file="instance-controller.tsx" />
          </Button>
        </div>

        {showConfirm && <Button size="sm" onClick={confirm} className="absolute top-8  z-40 bg-primary text-primary-foreground">
            Применить
          </Button>}
      </div>
    </div>;
};